/**
 * useEstimateWorkflow — Encapsulates all estimate creation logic
 * Extracted from App.tsx to decouple from the root component.
 */
import { useEstimates } from '../contexts/EstimatesContext';
import { useSettings } from '../contexts/SettingsContext';
import { Estimate, EstimateItem, EstimateStatus } from '../types';
import { makeDefaultProposalSettings } from '../services/proposalDefaults';
import { useIdioma } from '../i18n/idioma';

export const useEstimateWorkflow = () => {
    const { addEstimate, updateEstimate } = useEstimates();
    const { metrics, settings } = useSettings();
    const { idioma } = useIdioma();

    const createAndOpenEstimate = (
        clientId: string,
        clientName: string,
        type: string,
        initialItems: EstimateItem[],
        description?: string,
        tradeId?: string,
        scopeCategoryId?: string,
        scopeLabel?: string,
        /** Prospecto para el que se arma, cuando todavía no hay cliente. En ese
         *  caso `clientId` llega vacío a propósito: la propuesta cuelga del
         *  prospecto hasta que el trato se gana. */
        sourceLeadId?: string
    ): Estimate => {
        const initialTitle = scopeLabel
            ? `${scopeLabel} — ${clientName}`
            : `${type} Project for ${clientName}`;

        // P-6: one canonical default config, used for both the DB write and the local
        // editor object below (was duplicated as two thin literals).
        const defaultProposalSettings = makeDefaultProposalSettings({
            title: initialTitle,
            introMessage: description || `We are pleased to submit this proposal for your ${(scopeLabel || type).toLowerCase()} project.`,
            settings,
            idioma,
        });

        // B-1: do NOT seed a zone from scopeLabel. Seeding the category/scope as a
        // zone produced a phantom empty zone that then duplicated with the
        // template-named zone the builder auto-creates on the first template/
        // calculator add (useEstimateForm.addTemplateItems). The scope is already
        // preserved on the estimate via scopeCategoryId/scopeLabel (and pre-selects
        // the Templates tab); the first add names the zone correctly. A user who
        // wants an empty zone up front can add one via the builder's "+ Add zone".
        const initialZones = undefined;

        // 1. Crear la fila — UNA sola escritura.
        //
        // El oficio, el alcance y la configuración de la propuesta iban en un
        // `updateEstimate` disparado justo después de este INSERT. Cuando ese
        // PATCH le ganaba la carrera al INSERT, el `.eq('id', …)` no encontraba
        // ninguna fila, PostgREST contestaba 204 con `error: null`, y el
        // estimado se quedaba sin oficio ni alcance sin que nadie viera un
        // fallo. Ahora todo entra en el mismo insert, igual que `sourceLeadId`
        // —el ÚNICO vínculo entre la propuesta y la persona para la que se
        // hizo— que ya viajaba aquí por esta misma razón.
        const newId = addEstimate({
            clientId,
            clientName,
            items: initialItems,
            total: initialItems.reduce((sum, i) => sum + i.total, 0),
            validUntil: new Date(Date.now() + 1209600000).toISOString(),
            tradeId,
            scopeCategoryId,
            scopeLabel,
            zones: initialZones,
            proposalSettings: defaultProposalSettings,
            ...(sourceLeadId ? { sourceLeadId } : {}),
        });

        // 2. Build a local object for immediate use in Estimate Editor
        const createdEstimate: Estimate = {
            id: newId,
            clientId,
            clientName,
            items: initialItems,
            total: initialItems.reduce((sum, i) => sum + i.total, 0),
            pricingSnapshot: {
                overheadPerManHour: metrics.overheadPerManHour,
                laborBurdenPercent: metrics.avgLaborBurdenPercent,
                targetMarginPercent: settings.pricing.targetMarginPercent,
                baseLaborCost: metrics.avgHourlyWage,
                targetHourlyRate: metrics.targetHourlyRate,
                materialMarkupPercent: settings.pricing.defaultMaterialMarkupPercent,
                breakEvenRate: metrics.breakEvenRate
            },
            tradeId,
            scopeCategoryId,
            scopeLabel,
            sourceLeadId,
            status: EstimateStatus.DRAFT,
            dateCreated: new Date().toISOString(),
            lastUpdated: new Date().toISOString(),
            validUntil: new Date(Date.now() + 1209600000).toISOString(),
            zones: initialZones,
            adjustments: {
                tripCharge: false,
                emergencySurcharge: false,
                applyTax: false,
                taxRate: 8.25,
                taxLabor: settings.pricing.taxLaborDefault,
                minJobFeeApplied: false,
                discount: 0,
                discountType: 'flat' as const
            },
            proposalSettings: defaultProposalSettings
        };

        return createdEstimate;
    };

    return { createAndOpenEstimate, updateEstimate };
};
